import { Container, Graphics, InteractionEvent } from "pixi.js"
import { Callback, Coordinate, EmberMinimapDot, EmberMinimapState, EntityId, TileSize } from "game-common/models"
import { clamp, isMobile } from "../../../../client_util"
import { CloseButton } from "./close_button"

// Frame thickness (matches the follow-cam PiP, which shares this HUD slot) and the largest the map
// SURFACE may get. The longer room axis maps to MAP_MAX and the shorter one shrinks in proportion,
// so the map always carries the room's own aspect ratio -- an ember camp is square, but nothing here
// assumes that.
const FRAME = 4
export const MAP_MAX = 176
const CORNER = 6

// The collapse control, hung off the map's top-right corner the way a dialog hangs its close button.
// Smaller than a dialog's on a pointer, because it is dismissing a HUD panel rather than a modal --
// but NOT under a thumb, where 8 stage pixels is about 10 CSS pixels of target and essentially
// unhittable. Everything that places it measures from this constant, so it costs no layout.
const COLLAPSE_RADIUS = isMobile() ? 15 : 8

// Dot sizes and colours. White = camp member, red = wave attacker, bright green = you (the same
// green the world minimap has always used for the player). Trees are a DIM green wash painted under
// all of them: terrain, not a dot, and far enough from the player's bright green to never be mistaken
// for it.
const DOT_R = 2.2
const PLAYER_DOT_R = 2.8
const MEMBER_COLOR = 0xffffff
const ATTACKER_COLOR = 0xff3b30
const PLAYER_COLOR = 0x39ff6a
const TREE_COLOR = 0x2f6b3d
// Another person in the camp: a player, so player-sized and player-green, but a cooler, dimmer
// green than your own bright one. Near enough to read as "one of us" at a glance, far enough apart
// that you never mistake theirs for the dot you are steering.
const PARTNER_COLOR = 0x2bc4a8
// A stranger at the fire asking to be taken on. Ember gold -- the colour of the thing that drew
// them -- and the ONLY dot on this map that pulses. Nothing else here moves except by travelling,
// so the pulse alone says "this one is new, and it is waiting on you" with no legend to read. It
// also keeps a visitor clear of the red it would otherwise be mistaken for at this dot size: the
// other thing that turns up at your camp uninvited is a wave.
const VISITOR_COLOR = 0xffb648
const VISITOR_PULSE_MS = 1100
// The same stranger, earlier: still crossing the dark towards the fire. The same gold, because it is
// the same creature and nothing has changed about what it wants -- and NOT pulsing, because it is
// not asking yet. So the arrival reads as one continuous thing: a gold dot appears at the edge of
// the map, walks in, and starts to beat when it reaches the fire and puts its hand up.
const APPROACHING_ALPHA = 0.82
// How far the pulse swells the dot. Deliberately modest: the dots already GLIDE a full second
// between snapshots, and a big throb on top of a glide reads as jitter rather than as a beacon.
const VISITOR_PULSE_GROW = 0.55

// How much of the world the map lets through. The BACKDROP, its border and the tree wash are all
// semi-transparent so the map sits lightly over the game rather than punching a panel out of it; the
// dots stay fully opaque, because they are the thing the map is for.
const PANEL_COLOR = 0x05070a
const PANEL_ALPHA = 0.59
const BORDER_ALPHA = 0.36
const TREE_ALPHA = 0.61

// How long a dot takes to travel to a freshly-received position. Matched to the EMBER_MINIMAP
// campaign's broadcast interval, so a dot finishes its glide just as the next snapshot lands -- the
// map moves continuously off a feed that only speaks once a second.
export const DOT_GLIDE_MS = 1000

// One dot's journey between two snapshots. `t` is how far into the glide we are, in ms.
interface DotTrack {
    fromX: number
    fromY: number
    toX: number
    toY: number
    t: number
}

// Where a track is right now. Linear, not eased: the entity really did walk at a steady pace between
// the two samples, and easing would invent an acceleration that never happened.
export const trackPosition = (track: DotTrack): Coordinate => {
    const p = DOT_GLIDE_MS > 0 ? Math.max(0, Math.min(1, track.t / DOT_GLIDE_MS)) : 1
    return {
        x: track.fromX + (track.toX - track.fromX) * p,
        y: track.fromY + (track.toY - track.fromY) * p,
    }
}

// What counts as a tree on the map. Matched on the tile's ENTITY-TILE type name rather than a list of
// raw tile ids, so it holds across tilesets and biomes: the game's tree entity tiles are tree1..tree5,
// their damaged tree1Small/tree2Small forms, smallTree1 and palmTree1..6 (see
// "Project Samsara Items - entitytile.csv", and the canonical list the forager chops in
// ember-forager.js). A substring test rather than an enumeration so a tree type added later is picked
// up for free. Deliberately does NOT match "sapling" -- a seedling is not yet canopy.
export const isTreeEntityTile = (entityTile?: string): boolean => !!entityTile && /tree/i.test(entityTile)

// The minimum a tile map has to offer for the tree scan -- so the scan can be tested without standing
// up a whole TileMapMetaProvider. `getEntityTileAt` already accounts for felled trees (a removed
// tile is deleted from the layer map), which is why the scan reads it rather than the raw tile ids.
export interface TreeTileSource {
    getEntityTileAt: (x: number, y: number) => { entityTile?: string } | null | undefined
}

// Every tile cell in the room holding a tree, in TILE coordinates. The client already has the whole
// room's tile map, so trees never have to ride the wire -- only the moving things do.
//
// O(tiles) with a tile-meta lookup per cell, so the caller keys this on the map's collision version
// and re-runs it only when the room's tiles actually change (a felled tree, a placed wall).
export const collectTreeCells = (map: TreeTileSource, tilesWide: number, tilesHigh: number): Coordinate[] => {
    const cells: Coordinate[] = []
    if (!map || !(tilesWide > 0) || !(tilesHigh > 0)) {
        return cells
    }
    for (let x = 0; x < tilesWide; x++) {
        for (let y = 0; y < tilesHigh; y++) {
            if (isTreeEntityTile(map.getEntityTileAt(x, y)?.entityTile)) {
                cells.push({ x, y })
            }
        }
    }
    return cells
}

// Where a dot at fine room coordinate (x, y) lands on a map surface `mapW` x `mapH` px, given a room
// `width` x `height` fine units across. Pure, and returns surface-local px (the caller adds FRAME).
// Clamped so a body that straddles the room's outer edge still shows ON the map rather than under
// the frame.
export const projectDot = (
    dot: Coordinate,
    room: { width: number; height: number },
    mapW: number,
    mapH: number,
): Coordinate => {
    const fx = room.width > 0 ? dot.x / room.width : 0
    const fy = room.height > 0 ? dot.y / room.height : 0
    return {
        x: Math.max(0, Math.min(1, fx)) * mapW,
        y: Math.max(0, Math.min(1, fy)) * mapH,
    }
}

// The map surface size for a room of the given fine extent: the longer axis gets MAP_MAX, the other
// keeps the room's aspect ratio. A degenerate/unknown extent falls back to a square.
export const surfaceSizeFor = (room: { width: number; height: number }): { w: number; h: number } => {
    if (!(room.width > 0) || !(room.height > 0)) {
        return { w: MAP_MAX, h: MAP_MAX }
    }
    const longest = Math.max(room.width, room.height)
    return {
        w: Math.round((room.width / longest) * MAP_MAX),
        h: Math.round((room.height / longest) * MAP_MAX),
    }
}

// Keep a free-floating HUD panel wholly inside `bounds`, in whatever coordinates the owner works in.
// Pure: WHICH edges a panel may touch is the owner's business, but the arithmetic is not. The max()
// guards a panel bigger than the space it is in, which would otherwise hand clamp() a range with its
// ends the wrong way round.
export const clampPanelWithin = (
    at: Coordinate,
    panel: { width: number; height: number },
    bounds: { left: number; top: number; right: number; bottom: number },
): Coordinate => ({
    x: clamp(at.x, bounds.left, Math.max(bounds.left, bounds.right - panel.width)),
    y: clamp(at.y, bounds.top, Math.max(bounds.top, bounds.bottom - panel.height)),
})

// What a panel being dragged asks of its owner. The widget knows where the pointer has taken it; the
// OWNER owns HUD geometry -- and is what re-anchors the panel above the roster strip, so it would
// otherwise snatch the map back mid-drag -- which is why it does the clamping and the actual move.
export interface EmberMinimapDragHandlers {
    // The press landed on the panel, before any movement. Lets the owner hold off the world's
    // click-to-attack for the length of the press.
    onGrab: Callback
    // Where the player is asking the panel to go, in the panel's PARENT coordinates.
    onDragTo: (x: number, y: number) => void
    // The press ended -- which the pointer may well have done somewhere else entirely.
    onRelease: Callback
}

// The ember room's minimap: a framed scale plan of the whole camp room, showing the forest it sits in
// (dim green), a dot for every camp member (white) and every live wave attacker (red), plus the
// player (bright green). It takes the HUD slot directly above the roster strip -- the same slot as
// the follow-cam PiP, which wins it whenever the player is actively tracking a roster member (see
// GuiManager.positionEmberMinimap).
//
// The MOVING things are fed by the server (Protocol.type_updateEmberMinimap) rather than read off the
// client's own entities, because the whole point is the attacker crossing the far side of the room --
// which the world-state sync only refreshes about once a second at that distance. The trees are the
// opposite case: the client already holds the room's entire tile map, so they are scanned locally and
// never sent.
//
// Four layers, so the cheap thing happens often and the costly thing happens rarely:
//   frame     drawn only when the room's extent changes
//   trees     drawn only when the room's TILES change (felled tree, placed wall) -- the big one
//   dots      redrawn each frame, but ONLY while something is actually gliding (see animate)
//   playerDot drawn once, then just REPOSITIONED every frame (no geometry churn)
//
// The panel is DRAGGABLE: the corner of the camp it covers is sometimes the corner the player needs to
// watch, so the whole map is its own grab handle (there is nothing on it to click but the collapse
// control) and it can be parked anywhere on screen. See EmberMinimapDragHandlers for the division of
// labour with GuiManager.
export class EmberMinimapWidget extends Container {
    private frame = new Graphics()
    private trees = new Graphics()
    private dots = new Graphics()
    private playerDot = new Graphics()
    private state: EmberMinimapState | null = null
    private mapW = MAP_MAX
    private mapH = MAP_MAX
    // The last tree scan, kept so the layer can be repainted if the map's extent changes under it.
    private treeCells: Coordinate[] = []
    private treeTilesWide = 0
    private treeTilesHigh = 0
    // Where each dot is travelling, keyed by its entity id -- the whole reason the dots carry one.
    // Kept per kind because a camper never becomes an attacker, and it saves storing the colour.
    private memberTracks: Map<EntityId, DotTrack> = new Map()
    private attackerTracks: Map<EntityId, DotTrack> = new Map()
    // The other PEOPLE in the camp. Glided like the rest -- they come off the same once-a-second
    // feed -- and kept apart from `members` so they can be drawn last, bigger and in their own
    // green. The viewer is never in here; the server leaves them out, and their own dot is
    // `playerDot`, repositioned every frame off the entity the client already tracks.
    private partnerTracks: Map<EntityId, DotTrack> = new Map()
    // Strangers at the fire. Tracked exactly like everyone else (so their dot glides rather than
    // teleports between snapshots); what is different is only how it is drawn.
    private visitorTracks: Map<EntityId, DotTrack> = new Map()
    // Strangers still on their way to it. A separate map rather than a flag on the visitor track,
    // so each kind is plotted by one `plot` call with its own treatment. The same id moves from one
    // map to the other when the recruit reaches the fire; its glide restarts there, which costs
    // nothing visible because by then it has stopped walking.
    private approachingTracks: Map<EntityId, DotTrack> = new Map()
    // Free-running phase for the visitor pulse, so two of them beat together rather than each on
    // its own clock.
    private visitorPulseMs = 0
    // The extent the frame was last drawn for, so a steady stream of broadcasts doesn't redraw it.
    private drawnExtent = ""

    // Whether the pointer currently has hold of the panel, and where it took hold: the panel's origin
    // MINUS the pointer, in PARENT coordinates. Fixed at pointerdown rather than recomputed per frame,
    // or a drag that ran into a screen edge and came back would leave the panel creeping out from
    // under the cursor.
    private grabbed = false
    private grabDx = 0
    private grabDy = 0
    private drag?: EmberMinimapDragHandlers

    // The control that folds the map away into its "show map" chip. Hung off the top-right corner,
    // so it is where a player looks to dismiss a panel and never over the map's own contents.
    private collapseButton: CloseButton

    // `onCollapse` is what the corner control calls and `drag` is who moves the panel; both optional
    // only so the widget can be stood up bare in a test.
    constructor(onCollapse?: Callback, drag?: EmberMinimapDragHandlers) {
        super()
        this.addChild(this.frame, this.trees, this.dots, this.playerDot)
        this.collapseButton = new CloseButton({ radius: COLLAPSE_RADIUS, onClick: onCollapse })
        this.addChild(this.collapseButton)
        this.drawPlayerDot()
        this.drawFrame()
        this.attachDrag(drag)
        this.visible = false
    }

    // Make the whole panel a grab handle. Deliberately NO hitArea of its own: the frame graphic
    // already IS the panel's shape, and pixi skips a container's children entirely once the pointer
    // falls outside an explicit hitArea -- which would strand the collapse control in its hover state
    // whenever the pointer left the map in one jump.
    //
    // pointermove/pointerup are bound here rather than on some capture layer because pixi dispatches
    // both to every interactive object regardless of what was hit (moveWhenInside is off), so a drag
    // keeps its stream of moves even while the pointer is way off the panel -- which it will be the
    // moment the panel is dragged up against a screen edge.
    private attachDrag = (drag?: EmberMinimapDragHandlers) => {
        this.drag = drag
        this.interactive = true
        this.cursor = "grab"
        this.on("pointerdown", this.onGrab)
        this.on("pointermove", this.onDragMove)
        this.on("pointerup", this.onRelease)
        this.on("pointerupoutside", this.onRelease)
    }

    // A press on the panel takes hold of it -- unless it landed on the collapse control, the one thing
    // up here that is not a grab handle. (pixi bubbles a child's pointerdown to its parent, and
    // `target` is the deepest INTERACTIVE object that was actually hit, so that is what tells them
    // apart.)
    private onGrab = (event: InteractionEvent) => {
        if (event.target !== this || !this.parent) {
            return
        }
        const pointer = event.data.getLocalPosition(this.parent)
        this.grabDx = this.x - pointer.x
        this.grabDy = this.y - pointer.y
        this.grabbed = true
        this.cursor = "grabbing"
        this.drag?.onGrab()
    }

    private onDragMove = (event: InteractionEvent) => {
        if (!this.grabbed || !this.parent) {
            return
        }
        const pointer = event.data.getLocalPosition(this.parent)
        this.drag?.onDragTo(pointer.x + this.grabDx, pointer.y + this.grabDy)
    }

    private onRelease = () => {
        if (!this.grabbed) {
            return
        }
        this.grabbed = false
        this.cursor = "grab"
        this.drag?.onRelease()
    }

    // Pin the collapse control INSIDE the map's top-right corner. A dialog hangs its close button
    // over the corner, but this panel is right-justified a few px from the edge of the screen, so a
    // button hanging off it would sit half in the margin. Tucked in instead.
    private positionCollapseButton = () => {
        this.collapseButton.x = this.getWidth() - COLLAPSE_RADIUS - 3
        this.collapseButton.y = COLLAPSE_RADIUS + 3
    }

    private drawFrame = () => {
        this.frame.clear()
        this.frame.beginFill(PANEL_COLOR, PANEL_ALPHA)
        this.frame.lineStyle(2, 0xffffff, BORDER_ALPHA)
        this.frame.drawRoundedRect(0, 0, this.mapW + FRAME * 2, this.mapH + FRAME * 2, CORNER)
        this.frame.endFill()
        this.positionCollapseButton()
    }

    private drawPlayerDot = () => {
        this.playerDot.clear()
        this.playerDot.beginFill(PLAYER_COLOR, 1)
        this.playerDot.drawCircle(0, 0, PLAYER_DOT_R)
        this.playerDot.endFill()
        this.playerDot.visible = false
    }

    // One cell-sized square per tree tile. Rects rather than circles because a forest is hundreds of
    // cells and a square is 2 triangles to a circle's dozens — and at ~3.5px a tile, a square IS the
    // honest shape for "this cell has canopy in it".
    private drawTrees = () => {
        this.trees.clear()
        if (this.treeCells.length < 1 || !(this.treeTilesWide > 0) || !(this.treeTilesHigh > 0)) {
            return
        }
        const cellW = this.mapW / this.treeTilesWide
        const cellH = this.mapH / this.treeTilesHigh
        // Overdrawn by a hair so neighbouring cells of one canopy read as a clump, not a grid.
        const w = cellW + 0.5
        const h = cellH + 0.5
        this.trees.beginFill(TREE_COLOR, TREE_ALPHA)
        for (const cell of this.treeCells) {
            this.trees.drawRect(FRAME + cell.x * cellW, FRAME + cell.y * cellH, w, h)
        }
        this.trees.endFill()
    }

    private plot = (tracks: Map<EntityId, DotTrack>, color: number, radius: number = DOT_R, alpha: number = 1) => {
        if (tracks.size < 1) {
            return
        }
        this.dots.beginFill(color, alpha)
        tracks.forEach(track => {
            const p = projectDot(trackPosition(track), this.state!, this.mapW, this.mapH)
            this.dots.drawCircle(FRAME + p.x, FRAME + p.y, radius)
        })
        this.dots.endFill()
    }

    private drawDots = () => {
        this.dots.clear()
        if (!this.state) {
            return
        }
        // Attackers last so an attacker standing on a defender still reads as the threat -- but
        // partners after even them: a person is never the thing you want hidden under a dot, and an
        // attacker ON a partner is read off the red crowding them rather than off the green going
        // missing.
        this.plot(this.memberTracks, MEMBER_COLOR)
        this.plot(this.attackerTracks, ATTACKER_COLOR)
        // Under the people: somebody walking in is news, but it is not yet a question, and it
        // crosses open ground where there is nothing to be hidden under anyway.
        this.plot(this.approachingTracks, VISITOR_COLOR, DOT_R, APPROACHING_ALPHA)
        this.plot(this.partnerTracks, PARTNER_COLOR, PLAYER_DOT_R)
        // Over everything, including a partner: a visitor is the one dot on this map that is a
        // question being asked of the player, and a question must never be the thing hidden under
        // somebody walking past it.
        if (this.visitorTracks.size > 0) {
            // 0 -> 1 -> 0 over the period, as one shared phase so two visitors beat together.
            const beat = 0.5 - 0.5 * Math.cos((this.visitorPulseMs / VISITOR_PULSE_MS) * Math.PI * 2)
            this.plot(this.visitorTracks, VISITOR_COLOR, DOT_R * (1 + VISITOR_PULSE_GROW * beat), 0.72 + 0.28 * beat)
        }
    }

    // Fold one snapshot's dots into their tracks: a dot seen before is re-aimed FROM wherever it has
    // glided to so far (never from its last reported position, which would snap it back), a dot seen
    // for the first time simply appears where it is, and a dot that has gone is dropped.
    private syncTracks = (tracks: Map<EntityId, DotTrack>, dots: EmberMinimapDot[]) => {
        const present = new Set<EntityId>()
        for (const dot of dots || []) {
            if (!dot || dot.id === undefined) {
                continue
            }
            present.add(dot.id)
            const track = tracks.get(dot.id)
            if (!track) {
                tracks.set(dot.id, { fromX: dot.x, fromY: dot.y, toX: dot.x, toY: dot.y, t: DOT_GLIDE_MS })
                continue
            }
            if (track.toX === dot.x && track.toY === dot.y) {
                // Standing still (or still on its way to this very spot) -- leave the glide alone
                // rather than restarting it, which would stall a dot that hasn't arrived yet.
                continue
            }
            const at = trackPosition(track)
            track.fromX = at.x
            track.fromY = at.y
            track.toX = dot.x
            track.toY = dot.y
            track.t = 0
        }
        for (const id of Array.from(tracks.keys())) {
            if (!present.has(id)) {
                tracks.delete(id)
            }
        }
    }

    // Advance every glide by a frame, and repaint the dots only if any of them actually moved. A camp
    // standing still costs nothing.
    animate = (dtMs: number) => {
        if (!this.state) {
            return
        }
        let moved = false
        const advance = (tracks: Map<EntityId, DotTrack>) =>
            tracks.forEach(track => {
                if (track.t < DOT_GLIDE_MS) {
                    track.t = Math.min(DOT_GLIDE_MS, track.t + dtMs)
                    moved = true
                }
            })
        advance(this.memberTracks)
        advance(this.attackerTracks)
        advance(this.partnerTracks)
        advance(this.visitorTracks)
        advance(this.approachingTracks)
        // A visitor's dot breathes whether or not anybody moved, so its presence is what makes this
        // map repaint every frame. That is the only case: with nobody at the fire -- which is the
        // camp almost all of the time -- a still camp still costs nothing.
        if (this.visitorTracks.size > 0) {
            this.visitorPulseMs = (this.visitorPulseMs + dtMs) % VISITOR_PULSE_MS
            moved = true
        }
        if (moved) {
            this.drawDots()
        }
    }

    // Take the latest snapshot (null clears the map, e.g. on leaving the room).
    update = (state: EmberMinimapState | null) => {
        this.state = state
        if (!state) {
            this.dots.clear()
            this.trees.clear()
            this.treeCells = []
            this.treeTilesWide = 0
            this.treeTilesHigh = 0
            this.memberTracks.clear()
            this.attackerTracks.clear()
            this.partnerTracks.clear()
            this.visitorTracks.clear()
            this.approachingTracks.clear()
            this.playerDot.visible = false
            return
        }

        const extent = `${state.width}x${state.height}`
        if (extent !== this.drawnExtent) {
            const size = surfaceSizeFor(state)
            this.mapW = size.w
            this.mapH = size.h
            this.drawnExtent = extent
            this.drawFrame()
            // The tree layer is laid out in the surface's px, so it has to follow the surface.
            this.drawTrees()
        }

        this.syncTracks(this.memberTracks, state.members)
        this.syncTracks(this.attackerTracks, state.attackers)
        // Absent on an older server's snapshot; syncTracks reads a missing list as an empty one,
        // which correctly empties the partner tracks rather than freezing the last dots in place.
        this.syncTracks(this.partnerTracks, state.players)
        // Missing from an older server's snapshot; an absent list is an empty one, which correctly
        // empties the tracks rather than leaving a gold dot pulsing over nobody.
        this.syncTracks(this.visitorTracks, state.visitors)
        this.syncTracks(this.approachingTracks, state.approaching)
        this.drawDots()
    }

    // Replace the forest. `cells` are TILE coordinates in a room `tilesWide` x `tilesHigh` tiles; the
    // caller (GuiManager) scans them off the client's own tile map and only when it has changed.
    updateTrees = (cells: Coordinate[], tilesWide: number, tilesHigh: number) => {
        this.treeCells = cells || []
        this.treeTilesWide = tilesWide
        this.treeTilesHigh = tilesHigh
        this.drawTrees()
    }

    // Move the player's own dot. Called every frame with the client's live player location, which is
    // predicted locally and so smoother than anything the broadcast could carry.
    trackPlayer = (location?: Coordinate) => {
        if (!this.state || !location) {
            this.playerDot.visible = false
            return
        }
        const p = projectDot(location, this.state, this.mapW, this.mapH)
        this.playerDot.position.set(FRAME + p.x, FRAME + p.y)
        this.playerDot.visible = true
    }

    // Whether there is a room to draw at all -- GuiManager keeps the widget off screen until the
    // first broadcast arrives, and takes it down again on leaving the room.
    hasData = (): boolean => !!this.state

    // The room's extent in TILES, for the caller's tree scan. Zero until the first broadcast.
    tilesWide = (): number => (this.state ? Math.round(this.state.width / TileSize) : 0)
    tilesHigh = (): number => (this.state ? Math.round(this.state.height / TileSize) : 0)

    // Outer frame dimensions, for positioning against the rest of the HUD.
    getWidth = (): number => this.mapW + FRAME * 2
    getHeight = (): number => this.mapH + FRAME * 2

    // Test seam: the corner control that folds the map away.
    collapseControl = (): CloseButton => this.collapseButton

    // Test seam: whether the pointer currently has hold of the panel.
    isGrabbed = (): boolean => this.grabbed

    // Test seams: what the map is currently showing.
    memberCount = (): number => this.memberTracks.size
    attackerCount = (): number => this.attackerTracks.size
    visitorCount = (): number => this.visitorTracks.size
    approachingCount = (): number => this.approachingTracks.size
    // Where a dot is being drawn right now, in ROOM (fine) coordinates -- i.e. part-way through its
    // glide, not where the last snapshot put it.
    dotPosition = (id: EntityId): Coordinate | null => {
        const track =
            this.memberTracks.get(id) ||
            this.attackerTracks.get(id) ||
            this.partnerTracks.get(id) ||
            this.visitorTracks.get(id) ||
            this.approachingTracks.get(id)
        return track ? trackPosition(track) : null
    }
    treeCount = (): number => this.treeCells.length
    isTrackingPlayer = (): boolean => this.playerDot.visible
    playerDotPosition = (): Coordinate => ({ x: this.playerDot.x, y: this.playerDot.y })
}
